/**
 * Resolução de ACL por documento — 3 escopos.
 *
 * PARIDADE COM O LEGADO (docs/legacy-spec/ged.md §5)
 * ---------------------------------------------------
 * O legado agrega os três escopos com `MAX()` por flag, o que é OU lógico puro:
 *
 *   MAX(reader) ... FROM erp_document_permission
 *   WHERE user_id = :me
 *      OR (department_id IN (:meus) AND user_id = 0)
 *      OR (department_id = 0 AND user_id = 0 AND store_id = :minha)
 *
 * Consequências documentadas do legado, todas preservadas ou corrigidas de forma explícita:
 *
 *   1. Permissão efetiva = UNIÃO (mais permissiva) dos escopos que casam.  → PRESERVADO
 *   2. Não existe negação: `reader = 0` no escopo usuário não revoga
 *      `reader = 1` no escopo empresa.                                     → CORRIGIDO (DENY)
 *   3. Não existe precedência usuário > departamento > empresa.            → CORRIGIDO
 *   4. Flags agregadas independentemente: `READ` pode vir da empresa e
 *      `APPROVE` do usuário.                                               → PRESERVADO
 *   5. Documento é visível se o usuário tem AO MENOS UMA das 6 flags.      → PRESERVADO
 *   6. `JOIN` (não LEFT JOIN): documento sem nenhuma linha de ACL que case
 *      é invisível.                                                        → PRESERVADO
 *
 * DIVERGÊNCIA DELIBERADA — ver docs/divergences.md#D-ACL-01
 * O legado não conseguia expressar "todo o departamento vê, menos o Fulano".
 * Introduzimos `effect: DENY` e precedência por especificidade. Regras migradas
 * do legado nascem todas como ALLOW, então o comportamento observável de uma
 * base migrada é idêntico — a negação só existe para regras criadas no produto novo.
 */
import { AccessRuleEffect, AccessRuleScope, ALL_DOCUMENT_PERMISSION_FLAGS, } from '@qualicloud/contracts';
const NONE = Object.freeze(Object.fromEntries(ALL_DOCUMENT_PERMISSION_FLAGS.map((f) => [f, false])));
/** Especificidade: maior vence. USER > DEPARTMENT > ORGANIZATION. */
const SCOPE_SPECIFICITY = {
    [AccessRuleScope.USER]: 3,
    [AccessRuleScope.DEPARTMENT]: 2,
    [AccessRuleScope.ORGANIZATION]: 1,
};
function ruleApplies(rule, subject) {
    switch (rule.scope) {
        case AccessRuleScope.USER:
            return rule.userId === subject.userId;
        case AccessRuleScope.DEPARTMENT:
            return !!rule.departmentId && subject.departmentIds.includes(rule.departmentId);
        case AccessRuleScope.ORGANIZATION:
            // O filtro de organização já foi aplicado pelo RLS — toda regra que chega aqui
            // pertence à organização do sujeito.
            return true;
        default:
            return false;
    }
}
/**
 * Resolve as permissões efetivas de um sujeito sobre um documento.
 *
 * Algoritmo, por flag independentemente (preserva a regra 4 do legado):
 *   1. Filtra as regras que se aplicam ao sujeito.
 *   2. Para cada flag, considera apenas as regras que a mencionam.
 *   3. Vence a regra de MAIOR especificidade de escopo.
 *   4. Empate de especificidade → DENY vence ALLOW (fail-safe).
 *   5. Nenhuma regra menciona a flag → negado.
 */
export function resolveEffectivePermissions(rules, subject) {
    if (subject.bypassFlags?.length) {
        const bypass = new Set(subject.bypassFlags);
        return Object.freeze(Object.fromEntries(ALL_DOCUMENT_PERMISSION_FLAGS.map((f) => [f, bypass.has(f)])));
    }
    const applicable = rules.filter((r) => ruleApplies(r, subject));
    if (applicable.length === 0)
        return NONE;
    const result = { ...NONE };
    for (const flag of ALL_DOCUMENT_PERMISSION_FLAGS) {
        const mentioning = applicable.filter((r) => r.flags.includes(flag));
        if (mentioning.length === 0)
            continue;
        const maxSpecificity = Math.max(...mentioning.map((r) => SCOPE_SPECIFICITY[r.scope]));
        const winners = mentioning.filter((r) => SCOPE_SPECIFICITY[r.scope] === maxSpecificity);
        // Empate → DENY vence (fail-safe).
        result[flag] = !winners.some((r) => r.effect === AccessRuleEffect.DENY);
    }
    return Object.freeze(result);
}
/**
 * Visibilidade do documento na listagem.
 * Preserva a regra do legado: basta UMA das 6 flags (ged.md §5.4).
 */
export function isDocumentVisible(perms) {
    return ALL_DOCUMENT_PERMISSION_FLAGS.some((f) => perms[f]);
}
/**
 * Reproduz EXATAMENTE a agregação por MAX() do legado, sem negação e sem precedência.
 * Existe apenas para o ETL provar equivalência de comportamento na reconciliação
 * (plano §10.4) e para os testes de paridade. NÃO usar em runtime.
 */
export function legacyMaxAggregate(rules, subject) {
    const applicable = rules.filter((r) => ruleApplies(r, subject));
    const result = { ...NONE };
    for (const rule of applicable) {
        if (rule.effect === AccessRuleEffect.DENY)
            continue; // o legado não tinha DENY
        for (const flag of rule.flags)
            result[flag] = true;
    }
    return Object.freeze(result);
}
//# sourceMappingURL=acl.js.map